import React, { useEffect, useMemo, useRef } from 'react'
import TreeNodeRow from './TreeNodeRow'

/** A node in a {@link SelectionTree}. Leaf nodes (no children) are selectable. */
export interface SelectionTreeNode {
    /** Unique identifier. Leaf ids are used in `selectedLeafIds`. */
    id: string
    /** Display text. */
    label: string
    /** Optional pill label shown left of the text for groups, or muted prefix for leaves. */
    badge?: string
    /** Child nodes. Absence or empty array marks this node as a leaf. */
    children?: SelectionTreeNode[]
}

export const getAllLeafIds = (nodes: SelectionTreeNode[]): string[] => {
    const ids: string[] = []
    const walk = (node: SelectionTreeNode) => {
        if (!node.children?.length) {
            ids.push(node.id)
        } else {
            node.children.forEach(walk)
        }
    }
    nodes.forEach(walk)
    return ids
}

const getLeafIds = (node: SelectionTreeNode): string[] => {
    if (!node.children?.length) return [node.id]
    return node.children.flatMap(getLeafIds)
}

/** Returns only the leaf ids from `checkedKeys` using the provided set. */
export const filterToLeafIds = (checkedKeys: (string | number)[], leafIdSet: Set<string>): string[] =>
    checkedKeys.map(String).filter((id) => leafIdSet.has(id))

/** Props for {@link SelectionTree}. */
interface SelectionTreeProps {
    /** Tree data. Parent nodes expand; leaf nodes (no children) are checkable. */
    nodes: SelectionTreeNode[]
    /** Controlled list of selected leaf node ids. */
    selectedLeafIds: string[]
    /** Called after each selection change with the updated leaf id list. */
    onSelectionChange: (leafIds: string[]) => void
    /** When `true`, all parent nodes start expanded. Default `false`. */
    defaultExpandAll?: boolean
    className?: string
    /**
     * When `true`, after `selectedLeafIds` first becomes non-empty the component
     * scrolls the nearest scrollable ancestor to put the first selected row at
     * the top of the viewport. Default `false`.
     */
    scrollToFirstSelected?: boolean
    /**
     * Optional renderer for extra content shown to the right of each leaf node's label.
     * Called with the leaf node id; return `null` to render nothing for that node.
     */
    renderLeafExtra?: (nodeId: string) => React.ReactNode
}

const findScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
    let node: HTMLElement | null = el.parentElement
    while (node) {
        const { overflow, overflowY } = getComputedStyle(node)
        if (/auto|scroll/.test(overflow + overflowY)) return node
        node = node.parentElement
    }
    return null
}

/**
 * Checkbox tree using MUI Checkbox and Collapse. Supports arbitrary depth via recursion.
 * Parent nodes show checked/indeterminate state based on their leaf descendants,
 * and a `{selected}/{total}` count badge. Selection is controlled via `selectedLeafIds`;
 * expansion is uncontrolled (each node owns its own expanded state).
 */
const SelectionTree = ({
    nodes,
    selectedLeafIds,
    onSelectionChange,
    defaultExpandAll = false,
    className = '',
    scrollToFirstSelected = false,
    renderLeafExtra,
}: SelectionTreeProps) => {
    const checkedSet = useMemo(() => new Set(selectedLeafIds), [selectedLeafIds])

    const nodeLeafIdsMap = useMemo(() => {
        const map = new Map<string, string[]>()
        const walk = (node: SelectionTreeNode) => {
            map.set(node.id, getLeafIds(node))
            node.children?.forEach(walk)
        }
        nodes.forEach(walk)
        return map
    }, [nodes])

    const toggleNode = (key: string) => {
        const nodeLeafIds = nodeLeafIdsMap.get(key) ?? []
        if (nodeLeafIds.length === 0) return
        const allChecked = nodeLeafIds.every((id) => checkedSet.has(id))
        const nodeLeafSet = new Set(nodeLeafIds)
        if (allChecked) {
            onSelectionChange(selectedLeafIds.filter((id) => !nodeLeafSet.has(id)))
        } else {
            const merged = new Set(selectedLeafIds)
            nodeLeafIds.forEach((id) => merged.add(id))
            onSelectionChange(Array.from(merged))
        }
    }

    const rootRef = useRef<HTMLDivElement>(null)
    const didScrollRef = useRef(false)

    useEffect(() => {
        if (!scrollToFirstSelected || didScrollRef.current || !selectedLeafIds.length) return
        const root = rootRef.current
        if (!root) return
        const firstChecked = root.querySelector<HTMLElement>('input[type="checkbox"]:checked, input[type="checkbox"][data-indeterminate="true"]')
        const row = firstChecked?.closest('div')
        if (!row) return
        const scrollable = findScrollableAncestor(root)
        if (!scrollable) return
        didScrollRef.current = true
        scrollable.scrollTop += row.getBoundingClientRect().top - scrollable.getBoundingClientRect().top
    }, [selectedLeafIds, scrollToFirstSelected])

    return (
        <div ref={rootRef} className={className}>
            {nodes.map((node) => (
                <TreeNodeRow
                    key={node.id}
                    node={node}
                    depth={0}
                    checkedSet={checkedSet}
                    nodeLeafIdsMap={nodeLeafIdsMap}
                    onToggle={toggleNode}
                    defaultExpandAll={defaultExpandAll}
                    renderLeafExtra={renderLeafExtra}
                />
            ))}
        </div>
    )
}

export default SelectionTree
